'use client';

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Brain, Video, Edit, Info } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface ContextItem {
  type: 'selectedMoment' | 'activeMemo' | 'custom';
  icon?: React.ReactNode;
  title: string;
  subtitle?: string;
  details?: Array<{ label: string; value: string }>;
}

interface MomentoContextBlockProps {
  items: ContextItem[];
  defaultOpen?: boolean;
  className?: string;
}

export function MomentoContextBlock({
  items,
  defaultOpen = true,
  className
}: MomentoContextBlockProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  if (items.length === 0) return null;

  return (
    <div className={cn(
      "mb-3 rounded-lg border border-accent-blue-200 dark:border-accent-blue-800 bg-gradient-to-r from-accent-blue-50/50 to-blue-50/50 dark:from-accent-blue-950/20 dark:to-blue-950/20 overflow-hidden shadow-sm",
      className
    )}>
      {/* Header */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-accent-blue-100/30 dark:hover:bg-accent-blue-900/20 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Brain className="h-4 w-4 text-accent-blue-600 dark:text-accent-blue-400 animate-pulse" />
          <span className="text-sm font-medium text-momento-900 dark:text-momento-50">
            Momento is using context
          </span>
          <span className="text-xs text-accent-blue-600 dark:text-accent-blue-400">
            ({items.length} {items.length === 1 ? 'item' : 'items'})
          </span>
        </div>
        <ChevronDown
          className={cn(
            "h-4 w-4 text-accent-blue-600 dark:text-accent-blue-400 transition-transform",
            isOpen && "rotate-180"
          )}
        />
      </button>

      {/* Collapsible Content */}
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="border-t border-accent-blue-200 dark:border-accent-blue-800"
          >
            <div className="px-4 py-3 space-y-3">
              {items.map((item, index) => (
                <ContextItemDisplay key={index} item={item} />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function ContextItemDisplay({ item }: { item: ContextItem }) {
  const getIcon = () => {
    if (item.icon) return item.icon;

    switch (item.type) {
      case 'selectedMoment':
        return <Video className="h-4 w-4" />;
      case 'activeMemo':
        return <Edit className="h-4 w-4" />;
      default:
        return <Info className="h-4 w-4" />;
    }
  };

  return (
    <div className="flex gap-3 text-sm">
      <div className="flex-shrink-0 mt-0.5 text-accent-blue-600 dark:text-accent-blue-400">
        {getIcon()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-momento-900 dark:text-momento-50 truncate">
          {item.title}
        </div>
        {item.subtitle && (
          <div className="text-xs text-momento-700 dark:text-momento-300 mt-0.5">
            {item.subtitle}
          </div>
        )}
        {item.details && item.details.length > 0 && (
          <div className="mt-2 space-y-1">
            {item.details.map((detail, idx) => (
              <div key={idx} className="flex items-baseline gap-2 text-xs">
                <span className="text-accent-blue-600 dark:text-accent-blue-400 font-medium">
                  {detail.label}:
                </span>
                <span className="text-momento-800 dark:text-momento-200">
                  {detail.value}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
